import { useEffect, useRef } from 'react'

/**
 * Hook for tracking events that should only fire once per condition.
 *
 * Usage:
 * ```tsx
 * // Track once when data is available
 * useTrackOnce(
 *   () => trackAccommodationDetail(data),
 *   [data], // deps that trigger the tracking
 *   !!data  // condition that must be true to track
 * )
 *
 * // Track once unconditionally (on mount with deps)
 * useTrackOnce(
 *   () => trackPageView(),
 *   []
 * )
 * ```
 *
 * @param trackFn - The tracking function to call
 * @param deps - Dependencies array (like useEffect)
 * @param condition - Optional condition that must be true to trigger tracking (default: true)
 */
export function useTrackOnce(
  trackFn: () => void | Promise<void>,
  deps: React.DependencyList,
  condition: boolean = true,
): void {
  const hasTracked = useRef(false)

  useEffect(() => {
    if (!hasTracked.current && condition) {
      hasTracked.current = true
      void trackFn()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/**
 * Hook for tracking events that should fire when a key changes.
 * Useful for tracking changes to selections (e.g., date changes).
 *
 * Usage:
 * ```tsx
 * useTrackOnChange(
 *   `${dateFrom}-${dateTo}`, // key that identifies the selection
 *   () => trackDateSelection(dateFrom, dateTo),
 *   !!dateFrom && !!dateTo // condition to track
 * )
 * ```
 *
 * @param key - A string key that identifies the current state
 * @param trackFn - The tracking function to call when key changes
 * @param condition - Condition that must be true to trigger tracking
 */
export function useTrackOnChange(
  key: string | null,
  trackFn: () => void | Promise<void>,
  condition: boolean = true,
): void {
  const prevKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (condition && key !== null && prevKeyRef.current !== key) {
      prevKeyRef.current = key
      void trackFn()
    }
  }, [key, condition, trackFn])
}
